import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import {
  Box,
  Typography,
  Modal,
  Paper,
  Button,
  IconButton,
  Link,
  Avatar,
  Badge,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import AddIcon from "@mui/icons-material/Add";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Pin, PinOff } from "lucide-react";
import sh from "bundles/common/utils/sh";
import { humanizeRegion, getFullPortfolioURL } from "bundles/common/utils/utils";
import HiredPositionSelectionModal from "./HiredPositionSelectionModal";
import CloseJobApplicationModal from "./CloseJobApplicationModal";
import ConfirmModal from "./ConfirmModal";
import ContactCandidateModal from "./ContactCandidateModal";
import { replaceCurrentRecruiter } from "bundles/Recruiter/constants/recruiterConstants";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import EngineeringDefaultProfile from "../../../../app/assets/images/pictures/engineering_default_profile.png";
import ArchitectureDefaultProfile from "../../../../app/assets/images/pictures/architecture_default_profile.png";
import ConstructionDefaultProfile from "../../../../app/assets/images/pictures/construction_default_profile.png";

const DENIAL_REASON_OPTIONS = ["wrong_diploma", "wrong_xp", "insufficient_languages", "closed_position", "other"];

// Placeholder cover letters written when the talent team submits on a candidate's
// behalf. They carry nothing for the recruiter, so they're hidden. Compared
// accent- and case-insensitively, but otherwise word for word.
const TALENT_TEAM_COVER_LETTERS = [
  "sent by talent team",
  "envoye par l'equipe talent",
];

const isTalentTeamCoverLetter = (text) => {
  if (!text) return false;

  const normalized = text
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // drop accents: envoyé -> envoye
    .replace(/[‘’]/g, "'"); // curly apostrophes -> straight

  return TALENT_TEAM_COVER_LETTERS.includes(normalized);
};

const getSectorDefaultAvatar = (desiredSector) => {
  if (!desiredSector) return null;

  const sectorMap = {
    engineering: EngineeringDefaultProfile,
    architecture: ArchitectureDefaultProfile,
    construction: ConstructionDefaultProfile,
  };

  return sectorMap[desiredSector] || null;
};

const CandidateDialog = ({
  candidate,
  candidateLoading = false,
  jobApplication,
  jobApplications = [],
  onClose,
  isDialogOpen,
  setIsDialogOpen,
  onJobApplicationClosed = null,
  onJobApplicationReopened = null,
  onKanbanMetaUpdate = null,
  columns = [],
  currentColumnId = null,
  onMoveToColumn = null,
  isModal = false,
  enableCandidateReveal = false,
  blurCandidateReveal = true,
  showNotes = false,
  hideNotes = false,
  onAddNote = null,
  noteCount = 0,
  onPanelClose = null,
  showContactButton = true,
  scrollToTopOnCandidateChange = false,
}) => {
  const dispatch = useDispatch();
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const theme = useTheme();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [closingAppId, setClosingAppId] = useState(null);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [isHiredSelectionModalOpen, setIsHiredSelectionModalOpen] = useState(false);
  const [pendingHiredColumnId, setPendingHiredColumnId] = useState(null);
  const [selectedHiredAppId, setSelectedHiredAppId] = useState(null);
  const [isCloseApplicationModalOpen, setIsCloseApplicationModalOpen] = useState(false);
  const [pendingCloseApplication, setPendingCloseApplication] = useState(null);
  const [selectedDenialReasons, setSelectedDenialReasons] = useState([]);
  const [otherDenialReason, setOtherDenialReason] = useState("");
  const [isReopenApplicationModalOpen, setIsReopenApplicationModalOpen] = useState(false);
  const [pendingReopenApplication, setPendingReopenApplication] = useState(null);
  const [coverLetterApp, setCoverLetterApp] = useState(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactConversation, setContactConversation] = useState(null);
  const [contactConversationLoading, setContactConversationLoading] = useState(false);

  const [localJobApplications, setLocalJobApplications] = useState([]);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [justUnblurredCandidates, setJustUnblurredCandidates] = useState([]);
  const [noteContent, setNoteContent] = useState("");
  const [isAddingNote, setIsAddingNote] = useState(false);

  const dialogPaperRef = useRef(null);

  const currentColumn = useMemo(() => {
    return columns.find((col) => col.id === currentColumnId) || null;
  }, [columns, currentColumnId]);

  const availableColumns = useMemo(() => {
    return columns.filter((col) => col.id !== currentColumnId);
  }, [columns, currentColumnId]);

  const activeJobApplications = useMemo(() => {
    return localJobApplications.filter((app) => app.status !== "closed");
  }, [localJobApplications]);
  
  const handleClose = () => {
    setIsDialogOpen(false);
    setIsMoveModalOpen(false);
    setIsHiredSelectionModalOpen(false);
    setPendingHiredColumnId(null);
    setSelectedHiredAppId(null);
    setIsCloseApplicationModalOpen(false);
    setPendingCloseApplication(null);
    setSelectedDenialReasons([]);
    setOtherDenialReason("");
    setIsReopenApplicationModalOpen(false);
    setPendingReopenApplication(null);
    setIsContactModalOpen(false);
    if (onClose) onClose();
  };

  const openCloseApplicationModal = (appToClose) => {
    if (!appToClose?.id || loading || closingAppId) return;
    setPendingCloseApplication(appToClose);
    setSelectedDenialReasons(
      Array.isArray(appToClose?.denial_reasons)
        ? appToClose.denial_reasons.filter((reason) => DENIAL_REASON_OPTIONS.includes(reason))
        : []
    );
    setOtherDenialReason("");
    setIsCloseApplicationModalOpen(true);
  };

  const closeCloseApplicationModal = () => {
    if (closingAppId) return;
    setIsCloseApplicationModalOpen(false);
    setPendingCloseApplication(null);
    setSelectedDenialReasons([]);
    setOtherDenialReason("");
  };

  const toggleDenialReason = (reasonKey) => {
    setSelectedDenialReasons((prev) => {
      const isSelected = prev.includes(reasonKey);
      if (isSelected) {
        if (reasonKey === "other") setOtherDenialReason("");
        return prev.filter((item) => item !== reasonKey);
      }
      return [...prev, reasonKey];
    });
  };

  const handleMoveToColumn = async (columnId, hiredJobApplicationId = null) => {
    if (!columnId || loading) return;

    const targetColumn = columns.find((col) => col.id === columnId);
    const isMovingToHired = targetColumn?.key === "hired";
    
    if (isMovingToHired && activeJobApplications.length > 1 && !hiredJobApplicationId) {
      setPendingHiredColumnId(columnId);
      setSelectedHiredAppId(activeJobApplications[0]?.id || null);
      setIsMoveModalOpen(false);
      setIsHiredSelectionModalOpen(true);
      return;
    }
    
    setLoading(true);
    try {
      if (typeof onMoveToColumn === "function") {
        await onMoveToColumn(jobApplication, columnId, hiredJobApplicationId);
      }
      setIsMoveModalOpen(false);
      handleClose();
    } catch (err) {
      console.error(err);
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.move_failed" }), { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleHiredSelectionConfirm = async () => {
    if (!pendingHiredColumnId || !selectedHiredAppId || loading) return;
    
    setLoading(true);
    try {
      if (typeof onMoveToColumn === "function") {
        await onMoveToColumn(jobApplication, pendingHiredColumnId, selectedHiredAppId);
      }
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.marked_hired" }), { variant: "success" });
      handleClose();
    } catch (err) {
      console.error(err);
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.move_failed" }), { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // old handleMarkAsHired in case we need the button back 
  // const handleMarkAsHired = async () => {
  //   if (!hiredColumn || loading) return;
    
  //   if (activeJobApplications.length > 1) {
  //     setPendingHiredColumnId(hiredColumn.id);
  //     setSelectedHiredAppId(activeJobApplications[0]?.id || null);
  //     setIsHiredSelectionModalOpen(true);
  //     return;
  //   }
    
  //   setLoading(true);
  //   try {
  //     if (typeof onMoveToColumn === "function") {
  //       const singleAppId = activeJobApplications.length === 1 ? activeJobApplications[0].id : null;
  //       await onMoveToColumn(jobApplication, hiredColumn.id, singleAppId);
  //     }
  //     enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.marked_hired" }), { variant: "success" });
  //     handleClose();
  //   } catch (err) {
  //     console.error(err);
  //     enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.move_failed" }), { variant: "error" });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleCloseJobApplication = async (appToClose) => {
    if (!appToClose?.id || loading || closingAppId) return;

    const denialReasons = selectedDenialReasons
      .filter((reason) => reason !== "other")
      .concat(
        selectedDenialReasons.includes("other") && otherDenialReason.trim()
          ? [otherDenialReason.trim()]
          : selectedDenialReasons.includes("other")
            ? ["other"]
            : []
      );
    
    setClosingAppId(appToClose.id);
    
    setLocalJobApplications((prev) =>
      prev.map((app) => (app.id === appToClose.id ? { ...app, status: "closed" } : app))
    );
    
    try {
      let res = null;
      if (appToClose?.is_external) {
        res = await sh.put(`update_external_candidate_application_status/${appToClose.id}`, {
          authenticity_token: authenticityToken,
          external_candidate_id: appToClose?.external_candidate_id,
          status: "closed",
          denial_reasons: denialReasons,
        });
      } else {
        res = await sh.put(`update_job_application_status/${appToClose.id}`, {
          authenticity_token: authenticityToken,
          candidate_id: candidate?.id || appToClose?.candidate_id,
          status: "closed",
          denial_reasons: denialReasons,
        });
      }
      
      const payload = res?.data;
      const kanbanMeta = payload?.kanban;
      const kanbanCard = payload?.kanban_card;
      
      if (kanbanMeta && typeof onKanbanMetaUpdate === "function") {
        onKanbanMetaUpdate(kanbanMeta);
      }

      const updatedApps = localJobApplications.map((app) =>
        app.id === appToClose.id ? { ...app, status: "closed" } : app
      );
      const remainingActive = updatedApps.filter((app) => app.status !== "closed");
      
      if (typeof onJobApplicationClosed === "function") {
        onJobApplicationClosed({
          ...appToClose,
          status: "closed",
          kanban_card: kanbanCard,
          all_closed: remainingActive.length === 0,
        });
      }
      
      if (remainingActive.length === 0) {
        handleClose();
      }

      enqueueSnackbar(intl.formatMessage({ id: "applicant.closed_success" }), { variant: "success" });
    } catch (err) {
      console.error(err);
      setLocalJobApplications((prev) =>
        prev.map((app) => (app.id === appToClose.id ? { ...app, status: appToClose.status } : app))
      );
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.close_failed" }), { variant: "error" });
    } finally {
      setClosingAppId(null);
      setIsCloseApplicationModalOpen(false);
      setPendingCloseApplication(null);
      setSelectedDenialReasons([]);
      setOtherDenialReason("");
    }
  };

  const openReopenApplicationModal = (appToReopen) => {
    if (!appToReopen?.id || loading || closingAppId) return;
    setPendingReopenApplication(appToReopen);
    setIsReopenApplicationModalOpen(true);
  };

  const handleReopenJobApplication = async (appToReopen) => {
    if (loading || closingAppId) return;
    
    setClosingAppId(appToReopen.id);
    
    setLocalJobApplications((prev) =>
      prev.map((app) =>
        app.id === appToReopen.id ? { ...app, status: appToReopen?.is_external ? "open" : "pending" } : app
      )
    );
    
    try {
      let res = null;
      if (appToReopen?.is_external) {
        res = await sh.put(`update_external_candidate_application_status/${appToReopen.id}`, {
          authenticity_token: authenticityToken,
          external_candidate_id: appToReopen?.external_candidate_id,
          status: "open",
          denial_reasons: [],
        });
      } else {
        res = await sh.put(`update_job_application_status/${appToReopen.id}`, {
          authenticity_token: authenticityToken,
          candidate_id: candidate?.id || appToReopen?.candidate_id,
          status: "pending",
          denial_reasons: [],
        });
      }
      
      const payload = res?.data;
      const kanbanMeta = payload?.kanban;
      
      if (kanbanMeta && typeof onKanbanMetaUpdate === "function") {
        onKanbanMetaUpdate(kanbanMeta);
      }

      if (typeof onJobApplicationReopened === "function") {
        onJobApplicationReopened(payload?.data || appToReopen);
      }

      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.reopen_success" }), { variant: "success" });
    } catch (err) {
      console.error(err);
      setLocalJobApplications((prev) =>
        prev.map((app) => (app.id === appToReopen.id ? { ...app, status: "closed" } : app))
      );
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.reopen_failed" }), { variant: "error" });
    } finally {
      setClosingAppId(null);
      setIsReopenApplicationModalOpen(false);
      setPendingReopenApplication(null);
    }
  };

  const candidateData = candidate || jobApplication?.candidate || {};

  const candidateId = candidateData?.id;
  const isGodview = !!currentRecruiter?.godview;
  const isCandidateRevealed =
    !enableCandidateReveal ||
    isGodview ||
    (!!candidateId &&
      currentRecruiter?.candidate_interests?.some(
        (interest) => interest.candidate_id === candidateId
      )) ||
    (candidateId && justUnblurredCandidates.includes(candidateId));
  const shouldBlurCandidateInfo =
    enableCandidateReveal && blurCandidateReveal && !isGodview && !isCandidateRevealed;

  useEffect(() => {
    if (!scrollToTopOnCandidateChange) return;
    if (!isDialogOpen) return;
    if (!candidateId) return;
    if (!dialogPaperRef.current) return;

    try {
      dialogPaperRef.current.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      dialogPaperRef.current.scrollTop = 0;
    }
  }, [candidateId, scrollToTopOnCandidateChange, isDialogOpen]);

  const revealCandidate = useCallback(async () => {
    if (!enableCandidateReveal) return;
    if (!candidateId) return;

    try {
      await sh.post("/recruiter/reveal_candidate", {
        authenticity_token: authenticityToken,
        candidate_id: candidateId,
      });

      setJustUnblurredCandidates((prev) => {
        if (prev.includes(candidateId)) return prev;
        return [...prev, candidateId];
      });

      replaceCurrentRecruiter(dispatch, authenticityToken, () => {});
    } catch (err) {
      console.error(err);
      enqueueSnackbar(
        intl.formatMessage({
          id: "flash.error.generic",
          defaultMessage: "Failed to reveal candidate",
        }),
        { variant: "error" }
      );
    }
  }, [
    enableCandidateReveal,
    candidateId,
    authenticityToken,
    dispatch,
    enqueueSnackbar,
    intl,
  ]);

  const trackDownload = useCallback(
    (kind) => {
      if (!candidateId) return;
      sh
        .post("/recruiter/track_candidate_download", {
          authenticity_token: authenticityToken,
          candidate_id: candidateId,
          kind,
        })
        .catch(() => {});
    },
    [candidateId, authenticityToken]
  );

  const isExternalCandidate = Boolean(
    candidateData?.is_external ||
    jobApplication?.is_external ||
    jobApplications.some((app) => app?.is_external)
  );
  const canMessageCandidate = showContactButton && Boolean(candidateId) && !isExternalCandidate;
  const noteRecordType = isExternalCandidate ? "ExternalCandidate" : "Candidate";
  const hasCompanyLink = isExternalCandidate || jobApplication || jobApplications.length > 0;
  const noteRecordId = hasCompanyLink ? candidateData?.id : null;
  const applicationResumeUrl =
    localJobApplications.find((app) => app?.resume_url)?.resume_url ||
    (jobApplications || []).find((app) => app?.resume_url)?.resume_url ||
    (jobApplication?.job_applications || []).find((app) => app?.resume_url)?.resume_url;
  const resumeUrl = jobApplication?.resume_url || applicationResumeUrl || candidateData?.resume_url;
  const portfolioUrl = jobApplication?.portfolio_url || candidateData?.portfolio_url;
  const missingValueLabel = intl.formatMessage({ id: "candidate.job_title_missing" });
  const candidateFullName = [candidateData?.first_name, candidateData?.last_name].filter(Boolean).join(" ");
  const candidateTitle = candidateData.job_title || candidateData.desired_job_title || "";
  // Same as PreferencesView on find_candidate / CandidateSummary: merge region + desired_locations.
  const desiredLocationCodes = (() => {
    const fromDesired = Array.isArray(candidateData.desired_locations)
      ? candidateData.desired_locations.map((l) => (typeof l === "string" ? l : l?.label)).filter(Boolean)
      : [];
    if (candidateData.region) {
      return [...new Set([...fromDesired, candidateData.region].filter(Boolean))];
    }
    return fromDesired;
  })();
  const desiredLocationsLabel =
    desiredLocationCodes.length > 0
      ? desiredLocationCodes
        .map((code) =>
          intl.formatMessage({
            id: `regions.${code}`,
            defaultMessage: humanizeRegion(code),
          })
        )
        .join(", ")
      : "";

  // desired_sector is an array of taxonomy keys. Interpolating it straight into the message id
  // produced "company.company_industry.engineering,architecture", and an empty array is truthy,
  // so it also rendered a bare "company.company_industry." instead of the missing-value label.
  const desiredSectorCodes = Array.isArray(candidateData.desired_sector)
    ? candidateData.desired_sector.filter(Boolean)
    : [candidateData.desired_sector].filter(Boolean);
  const desiredSectorLabel =
    desiredSectorCodes.length > 0
      ? desiredSectorCodes
        .map((code) =>
          intl.formatMessage({
            id: `company.company_industry.${code}`,
            defaultMessage: code,
          })
        )
        .join(", ")
      : "";

  const appsFromProps = useMemo(() => {
    if (jobApplications.length > 0) return jobApplications;
    if (jobApplication) return [jobApplication];
    return [];
  }, [jobApplications, jobApplication]);

  const appsKey = useMemo(() => {
    return (appsFromProps || [])
      .map((app) => `${app?.id}:${app?.status}`)
      .join(",");
  }, [appsFromProps]);

  const loadNotes = useCallback(async () => {
    if (!noteRecordId) return;

    setNotesLoading(true);
    try {
      const res = await sh.get("candidate_notes", {
        params: {
          record_type: noteRecordType,
          record_id: noteRecordId,
        },
      });
      setNotes(res?.data?.data || []);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.notes_fetch_failed", defaultMessage: "Failed to load notes" }), {
        variant: "error",
      });
    } finally {
      setNotesLoading(false);
    }
  }, [enqueueSnackbar, intl, noteRecordId, noteRecordType]);


  const handleCreateNote = async () => {
    const trimmedContent = noteContent.trim();
    if (!trimmedContent || notesSaving || !noteRecordId) return;

    setNotesSaving(true);
    try {
      const res = await sh.post("candidate_notes", {
        authenticity_token: authenticityToken,
        record_type: noteRecordType,
        record_id: noteRecordId,
        content: trimmedContent,
        pinned: false,
      });
      const createdNote = res?.data?.data;
      if (createdNote) {
        setNotes((prev) => [createdNote, ...prev].sort((a, b) => {
          if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
          return new Date(b.created_at) - new Date(a.created_at);
        }));
      }
      setNoteContent("");
      setIsAddingNote(false);
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.note_added", defaultMessage: "Note added" }), { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.note_add_failed", defaultMessage: "Failed to add note" }), { variant: "error" });
    } finally {
      setNotesSaving(false);
    }
  };

  const handleTogglePinned = async (note) => {
    try {
      const res = await sh.patch(`candidate_notes/${note.id}`, {
        authenticity_token: authenticityToken,
        pinned: !note.pinned,
      });
      const updatedNote = res?.data?.data;
      if (!updatedNote) return;

      setNotes((prev) => prev
        .map((item) => (item.id === updatedNote.id ? updatedNote : item))
        .sort((a, b) => {
          if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
          return new Date(b.created_at) - new Date(a.created_at);
        }));
    } catch (err) {
      console.error(err);
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.note_update_failed", defaultMessage: "Failed to update note" }), { variant: "error" });
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await sh.delete(`candidate_notes/${noteId}`, {
        data: { authenticity_token: authenticityToken },
      });
      setNotes((prev) => prev.filter((note) => note.id !== noteId));
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.note_deleted", defaultMessage: "Note deleted" }), { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.note_delete_failed", defaultMessage: "Failed to delete note" }), { variant: "error" });
    }
  };

  useEffect(() => {
    setLocalJobApplications((prev) => {
      const prevKey = (prev || []).map((app) => `${app?.id}:${app?.status}`).join(",");
      if (prevKey === appsKey) return prev;
      return appsFromProps;
    });
  }, [appsKey, appsFromProps]);

  useEffect(() => {
    if (!isDialogOpen || !noteRecordId) return;
    loadNotes();
  }, [isDialogOpen, noteRecordId, loadNotes]);

  const loadContactConversation = useCallback(() => {
    if (!canMessageCandidate) {
      setContactConversation(null);
      return;
    }

    setContactConversationLoading(true);
    sh.get("/recruiter/conversations/for_candidate", { params: { candidate_id: candidateId } })
      .then((res) => setContactConversation(res.data?.data || null))
      .catch((err) => {
        console.error(err);
        setContactConversation(null);
      })
      .finally(() => setContactConversationLoading(false));
  }, [canMessageCandidate, candidateId]);

  useEffect(() => {
    if (!isDialogOpen) return;
    loadContactConversation();
  }, [isDialogOpen, loadContactConversation]);

  // An existing conversation the recruiter can already see messages in goes straight
  // to the messaging page; otherwise the contact modal writes the first message.
  const hasExistingConversation = Boolean(contactConversation?.has_messages);

  const handleContactClick = () => {
    if (hasExistingConversation) {
      window.location.href = `/${i18nLocale}/recruiter?tab=messages&conversation=${contactConversation.id}`;
      return;
    }
    setIsContactModalOpen(true);
  };

  const dialogContent = (
    <Paper
      ref={dialogPaperRef}
      sx={{
        width: "100%",
        maxWidth: isModal ? "800px" : "100%",
        maxHeight: isModal ? "90vh" : "auto",
        overflow: "auto",
        borderRadius: "10px",
        outline: "none",
        position: "relative",
        mx: isModal ? 2 : 0,
        padding: "20px",
        overflowY: "auto",
        boxShadow: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        msOverflowStyle: "none",
        scrollbarWidth: "none",
      }}
    >
      {/* Close button - only show in modal mode */}
      {(isModal || onPanelClose) && (
        <IconButton
          onClick={isModal ? handleClose : onPanelClose}
          sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
          disableFocusRipple
          disableRipple
        >
          <CloseIcon />
        </IconButton>
      )}

      {/* Add a note - top right of the panel, left of the close button in modal mode */}
      {onAddNote && (
        <Tooltip title={intl.formatMessage({ id: "recruiter.kanban.add_note", defaultMessage: "Add private note" })} placement="left" arrow>
          <IconButton
            onClick={onAddNote}
            aria-label={intl.formatMessage({ id: "recruiter.kanban.add_note", defaultMessage: "Add private note" })}
            disableRipple
            sx={{
              position: "absolute",
              top: 12,
              right: isModal || onPanelClose ? 52 : 12,
              zIndex: 1,
              width: "36px",
              height: "36px",
              color: theme.palette.text.main,
              border: `1px solid ${theme.palette.text.main}`,
              "&:hover": {
                backgroundColor: "hsla(153, 68%, 25%, 0.06)",
                borderColor: "hsl(153, 68%, 25%)",
              },
            }}
          >
            <Badge
              badgeContent={noteCount}
              color="primary"
              overlap="circular"
              sx={{ "& .MuiBadge-badge": { fontSize: "10px", height: 16, minWidth: 16, right: -2, top: -2 } }}
            >
              <NoteAddOutlinedIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
      )}

      {/* Header */}
      <Box sx={{ pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "20px", mb: "8px" }}>
          <Avatar
            src={candidateData.profile_picture_url || getSectorDefaultAvatar(candidateData?.desired_sector?.[0])}
            sx={{
              width: 56,
              height: 56,
              borderRadius: "0%",
              ...((!candidateData.profile_picture_url && getSectorDefaultAvatar(candidateData?.desired_sector?.[0])) && { backgroundColor: "#9e9e9e" })
            }}
          >
            {[candidateData?.first_name?.[0], candidateData?.last_name?.[0]].filter(Boolean).join("")}
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ color: theme.palette.text.main, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }} gutterBottom>
              {candidateFullName || missingValueLabel}
            </Typography>
            <Typography variant="body1" sx={{ color: theme.palette.text.main, fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
              {candidateTitle || missingValueLabel}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {candidateData.region && (
            <Chip
              label={intl.formatMessage({
                id: `regions.${candidateData.region}`,
                defaultMessage: humanizeRegion(candidateData.region),
              })}
              size="small"
              variant="outlined"
              sx={{ borderColor: theme.palette.primary.green, color: theme.palette.text.main }}
            />
          )}
          {candidateData.experience_year && (
            <Chip label={intl.formatMessage({ id: "recruiter.candidate_experience_pastille" }, { count: candidateData.experience_year })} size="small" variant="outlined" sx={{ borderColor: theme.palette.primary.green, color: theme.palette.text.main }} />
          )}
          {candidateData?.new_last_sign_in_at && (() => {
            const diff = Date.now() - new Date(candidateData.new_last_sign_in_at).getTime();
            const days = Math.floor(diff / 86400000);
            const weeks = Math.floor(days / 7);
            const months = Math.floor(days / 30);
            const [value, unit] = days < 1
              ? [0, "day"]
              : days < 7
                ? [-days, "day"]
                : weeks < 5
                  ? [-weeks, "week"]
                  : [-months, "month"];
            const rtf = new Intl.RelativeTimeFormat(i18nLocale, { numeric: "auto" });
            return (
              <Chip
                label={`${intl.formatMessage({ id: "recruiter.last_login" })}: ${rtf.format(value, unit)}`}
                size="small"
                variant="outlined"
                sx={{ borderColor: theme.palette.primary.green, color: theme.palette.text.main }}
              />
            );
          })()}
        </Box>
      </Box>

      {/* Move to section */}
      <Box>     
        {currentColumn && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "5px 10px",
              mb: 1.5,
              borderRadius: "20px",
              bgcolor: theme.palette.primary.green,
              color: theme.palette.common.white,
            }}
          >
            <Typography variant="body2" fontWeight="medium">
              {currentColumn.name}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
          {isModal && (
            <Button
              size="small"
              disabled={loading}
              onClick={() => {
                setIsDialogOpen(false);
                setIsMoveModalOpen(true);
              }}
              sx={{ padding: "6px 24px", 
                borderRadius: "29px", 
                color: theme.palette.common.white, 
                backgroundColor: theme.palette.text.main,
                border: `1px solid ${theme.palette.secondary.grey_strong}`, 
                fontWeight: "800",
                marginRight: "auto",
                "&:hover": {
                  backgroundColor: theme.palette.text.dark,
                },
                display: availableColumns.length === 0 ? "none" : "block",
              }}
            >
              <Typography variant="h4" fontWeight="medium" sx={{ fontSize: "14px !important", fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                <FormattedMessage id="recruiter.kanban.select_column" defaultMessage="Move to column" />
              </Typography>
            </Button>
          )}

          {canMessageCandidate && (
            <Button
              size="small"
              disabled={contactConversationLoading}
              startIcon={<ChatBubbleOutlineOutlinedIcon />}
              onClick={handleContactClick}
              sx={{
                padding: "6px 24px",
                borderRadius: "29px",
                color: theme.palette.text.main,
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.text.main}`,
                "&:hover": {
                  backgroundColor: "hsla(153, 68%, 25%, 0.06)",
                  borderColor: "hsl(153, 68%, 25%)",
                },
              }}
            >
              <Typography variant="h4" fontWeight="medium" sx={{ fontSize: "14px !important", fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                {hasExistingConversation ? (
                  <FormattedMessage id="recruiter.messaging.open_conversation" defaultMessage="Open the conversation" />
                ) : (
                  <FormattedMessage id="recruiter.messaging.contact_candidate" defaultMessage="Contact candidate" />
                )}
              </Typography>
            </Button>
          )}
        </Box>

      </Box>

      {candidateLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Contact details */}
          <Box
            sx={{
              padding: "20px",
              border: "0.5px solid #000",
              marginTop: "8px",
              borderRadius: "10px",
              position: "relative",
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: "16px", fontFamily: "Segoe UI Semibold, system-ui, sans-serif", fontWeight: "800" }}>
              <FormattedMessage id="recruiter.kanban.contact" defaultMessage="Contact" />
            </Typography>
        
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "16px",
                filter: shouldBlurCandidateInfo ? "blur(2.5px)" : "none",
                pointerEvents: shouldBlurCandidateInfo ? "none" : "auto",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <EmailOutlinedIcon fontSize="small" color={theme.palette.text.light_grey} />
                  {candidateData.email ? (
                    <Link 
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`mailto:${candidateData.email}`} 
                      underline="always" color="inherit" 
                      sx={{ 
                        textUnderlineOffset: "4px",
                        maxWidth: { xs: "150px", md: "350px" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                      {candidateData.email}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ color: theme.palette.secondary.black }}>
                      {missingValueLabel}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <PhoneOutlinedIcon fontSize="small" color={theme.palette.text.light_grey} />
                  {candidateData.phone ? (
                    <Link href={`tel:${candidateData.phone}`} underline="always" color="inherit" sx={{ textUnderlineOffset: "4px" }}>
                      {candidateData.phone}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ color: theme.palette.secondary.black }}>
                      {missingValueLabel}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <LocationOnOutlinedIcon fontSize="small" color={theme.palette.secondary.black} />
                  <Typography variant="body2" sx={{ color: theme.palette.secondary.black }}>
                    <FormattedMessage id="recruiter.kanban.region" defaultMessage="Region" />:{" "}
                    {candidateData.region
                      ? intl.formatMessage({
                        id: `regions.${candidateData.region}`,
                        defaultMessage: humanizeRegion(candidateData.region),
                      })
                      : missingValueLabel}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <WorkOutlineIcon fontSize="small" color={theme.palette.secondary.black} />
                  <Typography variant="body2" sx={{ color: theme.palette.secondary.black }}>
                    <FormattedMessage id="recruiter.kanban.experience" defaultMessage="Experience" />:{" "}
                    {candidateData.experience_year ? intl.formatMessage({ id: "recruiter.candidate_experience_pastille"}, {count: candidateData.experience_year}) : missingValueLabel}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {shouldBlurCandidateInfo && (
              <Box
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  revealCandidate();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    revealCandidate();
                  }
                }}
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 1,
                  cursor: "pointer",
                  backgroundColor: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "4px 12px",
                    borderRadius: "999px",
                    backgroundColor: "rgba(255,255,255,0.9)",
                    border: `1px solid ${theme.palette.secondary.grey_strong}`,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                >
                  <AccessTimeOutlinedIcon sx={{ color: theme.palette.text.main, fontSize: "16px" }} />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 800, fontSize: "12px", color: theme.palette.text.main, whiteSpace: "nowrap" }}
                  >
                    <FormattedMessage id="candidate_search.click" defaultMessage="Click to reveal" />
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Preferences */}
          <Box
            sx={{
              padding: "20px",
              border: "0.5px solid #000",
              marginTop: "8px",
              borderRadius: "10px",
              position: "relative",
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: "16px", fontFamily: "Segoe UI Semibold, system-ui, sans-serif", fontWeight: "800" }}>
              <FormattedMessage id="recruiter.kanban.preferences" defaultMessage="Preferences" />
            </Typography>
        
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                mb: resumeUrl || portfolioUrl ? "16px" : "0px",
              }}
            >
              {candidateData.desired_job_title ? (
                <Typography variant="body2" sx={{ color: theme.palette.secondary.black }}>
                  <FormattedMessage id="recruiter.kanban.job" defaultMessage="Job" />: {candidateData.desired_job_title}
                </Typography>
              ) : null}
              <Typography variant="body2" sx={{ color: theme.palette.secondary.black }}>
                <FormattedMessage id="recruiter.kanban.desired_locations" defaultMessage="Desired locations" />:{" "}
                {desiredLocationsLabel || missingValueLabel}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.secondary.black }}>
                <FormattedMessage id="recruiter.kanban.sector" defaultMessage="Sector" />:{" "}
                {desiredSectorLabel || missingValueLabel}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                flexWrap: "wrap",
                alignItems: { xs: "stretch", md: "center" },
                gap: 1,
              }}
            >
              {resumeUrl && (
                <Button
                  startIcon={<DownloadIcon />}
                  href={resumeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  onClick={() => { if (enableCandidateReveal && !isCandidateRevealed) revealCandidate(); trackDownload("cv"); }}
                  sx={{
                    padding: "6px 24px",
                    borderRadius: "29px",
                    color: theme.palette.text.main,
                    border: `1px solid ${theme.palette.secondary.grey_strong}`,
                    width: { xs: "100%", md: "auto" },
                  }}
                >
                  <Typography variant="h4" sx={{ fontSize: "14px !important", fontFamily: "Segoe UI Semibold, system-ui, sans-serif", fontWeight: "800" }}>
                    <FormattedMessage id="recruiter.kanban.download_cv" defaultMessage="Download CV" />
                  </Typography>
                </Button>
              )}

              {portfolioUrl && (
                <Button
                  type="button"
                  size="small"
                  onClick={async (e) => {
                    e.preventDefault();
                    if (!portfolioUrl) return;
                    if (enableCandidateReveal && !isCandidateRevealed) {
                      await revealCandidate();
                    }
                    trackDownload("portfolio");
                    window.open(
                      getFullPortfolioURL(portfolioUrl),
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }}
                  sx={{
                    padding: "6px 24px",
                    borderRadius: "29px",
                    color: theme.palette.text.main,
                    border: `1px solid ${theme.palette.secondary.grey_strong}`,
                    width: { xs: "100%", md: "auto" },
                    textDecoration: "none",
                  }}
                >
                  <Typography variant="h4" sx={{ fontSize: "14px !important", fontFamily: "Segoe UI Semibold, system-ui, sans-serif", fontWeight: "800" }}>
                    <FormattedMessage id="recruiter.kanban.view_portfolio" defaultMessage="View portfolio" />
                  </Typography>
                </Button>
              )}
            </Box>

          </Box>


          {/* Job Applications */}
          {isModal && localJobApplications.length > 0 && (
            <Box sx={{ padding: "20px", border: "0.5px solid #000", marginTop: "8px", borderRadius: "10px" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: "Segoe UI Semibold, system-ui, sans-serif", fontWeight: "800" }}>
                  <FormattedMessage id="recruiter.kanban.job_applications" defaultMessage="Job Applications" />
                </Typography>
                {/* {!isClosed && currentColumn?.key !== "hired" && (
              <Button
                size="small"
                disabled={loading || !hiredColumn}
                onClick={handleMarkAsHired}
                sx={{
                  padding: "4px 16px",
                  borderRadius: "29px",
                  color: theme.palette.common.white,
                  backgroundColor: theme.palette.primary.main,
                  fontWeight: "700",
                  fontSize: "12px",
                  "&:hover": { backgroundColor: theme.palette.primary.dark },
                }}
              >
                <FormattedMessage id="recruiter.kanban.mark_hired" defaultMessage="Mark as Hired" />
              </Button>
            )} */}
              </Box>
        
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {localJobApplications.map((app) => {
              const isAppClosed = app.status === "closed";
              const isProcessing = closingAppId === app.id;

              return (
                <Box
                  key={app.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    padding: "10px 14px",
                    borderRadius: "8px",
                    backgroundColor: isAppClosed ? "rgba(0, 0, 0, 0.04)" : theme.palette.secondary.box,
                    opacity: isAppClosed ? 0.5 : 1,
                    transition: "opacity 0.2s ease",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                          color: theme.palette.text.strong,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {app.job_offer?.job_title || intl.formatMessage({ id: "recruiter.kanban.spontaneous_application" })}
                      </Typography>
                      {app.created_at && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: theme.palette.text.secondary,
                            fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          {new Date(app.created_at).toLocaleDateString(i18nLocale === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                        </Typography>
                      )}
                      {isAppClosed && (
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          <FormattedMessage id="recruiter.kanban.closed" defaultMessage="Closed" />
                        </Typography>
                      )}
                      {isAppClosed && app.denial_reasons?.length > 0 && (() => {
                        const formattedReasons = app.denial_reasons
                          .map((reason) =>
                            DENIAL_REASON_OPTIONS.includes(reason)
                              ? intl.formatMessage({ id: `applicant.denial_reasons.${reason}`, defaultMessage: reason })
                              : reason
                          )
                          .join(", ");
                        return (
                          <Tooltip title={formattedReasons} arrow placement="top">
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.error.main,
                                fontFamily: "Segoe UI Semibold, sans-serif",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "block",
                                maxWidth: "100%",
                                cursor: "default",
                              }}
                            >
                              {formattedReasons}
                            </Typography>
                          </Tooltip>
                        );
                      })()}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                      {/* One cover letter, whichever form it came in: an uploaded
                          document opens in a new tab, typed text opens in a modal. */}
                      {(app.cover_letter_document_url
                        || (app.cover_letter && !app.sent_from_talent_team && !isTalentTeamCoverLetter(app.cover_letter))) && (
                        <Button
                          size="small"
                          {...(app.cover_letter_document_url
                            ? { href: app.cover_letter_document_url, target: "_blank", rel: "noopener noreferrer" }
                            : { onClick: () => setCoverLetterApp(app) })}
                          startIcon={app.cover_letter_document_url
                            ? <InsertDriveFileOutlinedIcon sx={{ width: 14, height: 14 }} />
                            : <DescriptionOutlinedIcon sx={{ width: 14, height: 14 }} />}
                          sx={{
                            textTransform: "none",
                            fontSize: "11px",
                            fontFamily: "Segoe UI Semibold, sans-serif",
                            borderRadius: "999px",
                            padding: "2px 10px",
                            minWidth: "auto",
                            height: "30px",
                            lineHeight: 1.2,
                            color: theme.palette.primary.main,
                            border: `1px solid ${theme.palette.primary.main}`,
                            "&:hover": {
                              color: theme.palette.primary.dark,
                              borderColor: theme.palette.primary.dark,
                              backgroundColor: "rgba(20, 83, 45, 0.06)",
                            },
                          }}
                        >
                          <FormattedMessage id="application.cover_letter" defaultMessage="Cover letter" />
                        </Button>
                      )}
                      <Button
                        size="small"
                        disabled={isProcessing || (!isAppClosed && currentColumn?.key === "hired")}
                        onClick={() => (isAppClosed ? openReopenApplicationModal(app) : openCloseApplicationModal(app))}
                        startIcon={isAppClosed ? <ReplayIcon sx={{ width: 14, height: 14 }} /> : <CloseIcon sx={{ width: 14, height: 14 }} />}
                        sx={{
                          ml: 1,
                          textTransform: "none",
                          fontSize: "11px",
                          fontFamily: "Segoe UI Semibold, sans-serif",
                          borderRadius: "999px",
                          padding: "2px 10px",
                          minWidth: "auto",
                          height: "30px",
                          lineHeight: 1.2,
                          color: isAppClosed ? theme.palette.primary.main : theme.palette.text.secondary,
                          border: `1px solid ${isAppClosed ? theme.palette.primary.main : theme.palette.divider}`,
                          ...(!isAppClosed && currentColumn?.key === "hired" && { display: "none" }),
                          "&:hover": {
                            color: isAppClosed ? theme.palette.primary.dark : theme.palette.error.main,
                            borderColor: isAppClosed ? theme.palette.primary.dark : theme.palette.error.main,
                            backgroundColor: isAppClosed ? "rgba(20, 83, 45, 0.06)" : "rgba(211, 47, 47, 0.06)",
                          },
                        }}
                      >
                        {isAppClosed
                          ? intl.formatMessage({ id: "recruiter.kanban.reopen" })
                          : intl.formatMessage({ id: "recruiter.kanban.close" })
                        }
                      </Button>
                    </Box>
                  </Box>

                  {app.documents_details?.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                        pt: 1,
                        borderTop: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <InsertDriveFileOutlinedIcon
                          sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                            fontWeight: 800,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          <FormattedMessage
                            id="recruiter.kanban.additional_documents"
                            defaultMessage="Additional documents"
                          />
                        </Typography>
                      </Box>

                      {/* Same pill treatment as the "Download CV" / "View portfolio"
                          actions above, so every download in the dialog looks alike. */}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {app.documents_details.map((doc, idx) => (
                          <Button
                            key={idx}
                            size="small"
                            startIcon={<DownloadIcon sx={{ width: 16, height: 16 }} />}
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              padding: "4px 16px",
                              borderRadius: "999px",
                              textTransform: "none",
                              // These pills sit on the card's pale green background
                              // (secondary.box), so the grey border used by the
                              // buttons on the white dialog body would disappear here.
                              color: theme.palette.primary.main,
                              backgroundColor: theme.palette.common.white,
                              border: `1px solid ${theme.palette.primary.main}`,
                              maxWidth: "100%",
                              transition: "border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease",
                              // Inverts on hover, matching the theme's "primaryOutlined"
                              // button variant (Layouts/mui_components/button.js).
                              "&:hover": {
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.common.white,
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                                fontWeight: 800,
                                fontSize: "12px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                minWidth: 0,
                              }}
                            >
                              {doc.filename || intl.formatMessage({ id: "recruiter.kanban.document", defaultMessage: "Document" })}
                            </Typography>
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Notes */}
          {!hideNotes && (isModal || showNotes) && (
            <Box sx={{ padding: "20px", border: "0.5px solid #000", marginTop: "8px", borderRadius: "10px" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                  <LockOutlinedIcon sx={{ fontSize: "16px", color: theme.palette.text.secondary }} />
                  <Typography variant="subtitle1" sx={{ fontFamily: "Segoe UI Semibold, system-ui, sans-serif", fontWeight: "800" }}>
                    <FormattedMessage id="recruiter.kanban.private_notes" defaultMessage="Private notes" />
                  </Typography>
                </Box>
                {!isAddingNote && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setIsAddingNote(true)}
                    sx={{ borderRadius: "999px", textTransform: "none", fontFamily: "Segoe UI Semibold, system-ui, sans-serif", fontWeight: "800" }}
                    startIcon={<AddIcon fontSize="small" />}
                  >
                    <FormattedMessage id="recruiter.kanban.add" defaultMessage="Ajouter" />
                  </Button>
                )}
              </Box>

              <Typography variant="body2" sx={{ mb: 1.5, color: theme.palette.text.secondary }}>
                <FormattedMessage
                  id="recruiter.kanban.private_notes_hint"
                  defaultMessage="Internal to your team, never visible to the candidate."
                />
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {isAddingNote && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      value={noteContent}
                      onChange={(event) => setNoteContent(event.target.value)}
                      multiline
                      minRows={3}
                      maxRows={8}
                      fullWidth
                      placeholder={intl.formatMessage({ id: "recruiter.kanban.note_placeholder", defaultMessage: "Ecrire une note privée..." })}
                      disabled={notesSaving}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                          handleCreateNote();
                        }
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          alignItems: "flex-start",
                          "& fieldset": {
                            borderRadius: "8px",
                          },
                        },
                      }}
                    />
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <Button
                        size="small"
                        variant="text"
                        disabled={notesSaving}
                        onClick={() => {
                          setIsAddingNote(false);
                          setNoteContent("");
                        }}
                        sx={{ borderRadius: "999px", textTransform: "none" }}
                      >
                        <FormattedMessage id="recruiter.kanban.cancel" defaultMessage="Annuler" />
                      </Button>
                      <Button
                        size="small"
                        disabled={notesSaving || !noteContent.trim()}
                        onClick={handleCreateNote}
                        sx={{
                          padding: "6px 16px",
                          borderRadius: "999px",
                          textTransform: "none",
                          color: theme.palette.common.white,
                          backgroundColor: theme.palette.primary.main,
                          "&:hover": { backgroundColor: theme.palette.primary.dark },
                          "&.Mui-disabled": { color: theme.palette.common.white },
                        }}
                      >
                        <FormattedMessage id="recruiter.kanban.save" defaultMessage="Enregistrer" />
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
  
              {notesLoading ? (
                <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column" }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    <FormattedMessage id="recruiter.kanban.loading_notes" defaultMessage="Loading notes..." />
                  </Typography>
                </Box>
              ) : notes.length === 0 && !isAddingNote ? (
                <Box onClick={() => setIsAddingNote(true)} sx={{ mt: 1.5, display: "flex", flexDirection: "column" }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, cursor: "pointer" }}>
                    <FormattedMessage id="recruiter.kanban.no_notes" defaultMessage="Aucune note pour le moment" />
                  </Typography>
                </Box>
              ) : null}
  
              {!notesLoading && notes.map((note) => (
                <Box
                  key={note.id}
                  sx={{
                    position: "relative",
                    border: `1px solid ${theme.palette.secondary.grey}`,
                    borderRadius: "8px",
                    p: 1.5,
                    mb: 1,
                    backgroundColor: theme.palette.secondary.grey,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      bottom: 6,
                      width: 24,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <IconButton size="small" onClick={() => handleTogglePinned(note)} sx={{ p: 0.5 }}>
                      {note.pinned ? (
                        <Pin size={15} color={theme.palette.primary.main} />
                      ) : (
                        <PinOff size={15} color={theme.palette.text.secondary} />
                      )}
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteNote(note.id)} sx={{ p: 0.5 }}>
                      <DeleteOutlineIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                    </IconButton>
                  </Box>
  
                  <Box sx={{ pr: 6 }}>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "pre-wrap", color: theme.palette.text.strong, lineHeight: 1.4, fontWeight: 500 }}
                    >
                      {note.content}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                      <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                        {note.recruiter_name || intl.formatMessage({ id: "recruiter.kanban.unknown_author", defaultMessage: "Unknown recruiter" })}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.strong }}>
                      ·
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.strong }}>
                        {new Date(note.created_at).toLocaleString("fr-FR", {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Box>
                  </Box>
  
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Paper>
  );

  const moveToModal = (
    <Modal
      open={isMoveModalOpen}
      onClose={() => {
        setIsMoveModalOpen(false);
        setIsDialogOpen(true);
      }}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: "400px",
          borderRadius: "10px",
          outline: "none",
          mx: 2,
          overflow: "hidden",
          position: "relative",
          padding: "20px"
        }}
      >
        <IconButton
          onClick={() => {
            setIsMoveModalOpen(false);
            setIsDialogOpen(true);
          }}
          sx={{ position: "absolute", top: 12, right: 12, zIndex: 1 }}
          disableFocusRipple
          disableRipple
        >
          <CloseIcon />
        </IconButton>
        
        {/* Candidate Header */}
        <Box sx={{ pb: 1.5, display: "flex", alignItems: "center", gap: "24px" }}>
          <Avatar
            src={candidateData.profile_picture_url || getSectorDefaultAvatar(candidateData?.desired_sector?.[0])}
            sx={{
              width: 54,
              height: 54,
              ...((!candidateData.profile_picture_url && getSectorDefaultAvatar(candidateData?.desired_sector?.[0])) && { backgroundColor: "#9e9e9e" })
            }}
          >
            {[candidateData?.first_name?.[0], candidateData?.last_name?.[0]].filter(Boolean).join("")}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="600" sx={{ lineHeight: 1.3, color: theme.palette.text.main }} gutterBottom>
              {candidateFullName || missingValueLabel}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.main }}>
              {candidateTitle || missingValueLabel}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ pb: 1 }}>
          <Typography variant="h4" sx={{ fontSize: "20px !important", color: theme.palette.text.main, fontWeight: "800" }}>
            <FormattedMessage id="recruiter.kanban.move_to" defaultMessage="Move to" />
          </Typography>
        </Box>
        <List sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {columns.map((col) => {
            const isCurrentCol = col.id === currentColumnId;
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }} key={col.id}>
                <ListItemButton
                  onClick={() => !isCurrentCol && handleMoveToColumn(col.id)}
                  disabled={isCurrentCol}
                  sx={{
                    borderRadius: "20px",
                    p: "5px 10px",
                    backgroundColor: isCurrentCol ? theme.palette.primary.green : theme.palette.secondary.grey,
                    color: isCurrentCol ? theme.palette.common.white : theme.palette.text.main,
                    "&:hover": {
                      backgroundColor: isCurrentCol ? theme.palette.primary.green : "rgba(0, 0, 0, 0.04)",
                    },
                    "&.Mui-disabled": {
                      opacity: 1,
                      backgroundColor: theme.palette.primary.green,
                      color: theme.palette.common.white,
                    },
                  }}
                >
                  <ListItemText 
                    primary={col.name} 
                    primaryTypographyProps={{ 
                      fontSize: "13px",
                    }} 
                  />
                  <Chip
                    size="small"
                    label={col.count ?? 0}
                    sx={{
                      minWidth: "40px",
                      height: "24px",
                      borderRadius: "12px",
                      backgroundColor: isCurrentCol ? theme.palette.common.white : theme.palette.primary.green,
                      color: isCurrentCol ? theme.palette.text.main : theme.palette.common.white,
                      fontWeight: 700,
                      fontSize: "12px",
                      "& .MuiChip-label": {
                        px: 1,
                      },
                    }}
                  />
                </ListItemButton>
              </Box>
            );
          })}
        </List>
      </Paper>
    </Modal>
  );

  const hiredSelectionModal = (
    <HiredPositionSelectionModal
      open={isHiredSelectionModalOpen}
      onClose={() => {
        setIsHiredSelectionModalOpen(false);
        setPendingHiredColumnId(null);
        setSelectedHiredAppId(null);
        setIsMoveModalOpen(true);
      }}
      jobApplications={activeJobApplications}
      selectedJobApplicationId={selectedHiredAppId}
      onSelectJobApplication={setSelectedHiredAppId}
      onConfirm={handleHiredSelectionConfirm}
      loading={loading}
    />
  );

  const closeApplicationModal = (
    <CloseJobApplicationModal
      open={isCloseApplicationModalOpen}
      onClose={closeCloseApplicationModal}
      onConfirm={() => handleCloseJobApplication(pendingCloseApplication)}
      loading={Boolean(closingAppId)}
      candidateName={candidateFullName || missingValueLabel}
      jobTitle={
        pendingCloseApplication?.job_offer?.job_title ||
        intl.formatMessage({ id: "recruiter.kanban.spontaneous_application" })
      }
      denialReasons={selectedDenialReasons}
      otherReason={otherDenialReason}
      onToggleReason={toggleDenialReason}
      onChangeOtherReason={setOtherDenialReason}
    />
  );

  const reopenApplicationModal = (
    <ConfirmModal
      isOpen={isReopenApplicationModalOpen}
      setIsOpen={setIsReopenApplicationModalOpen}
      handleConfirm={() => handleReopenJobApplication(pendingReopenApplication)}
      labelId="recruiter.kanban.reopen_application_confirmation"
    />
  );

  const coverLetterModal = (
    <Modal
      open={Boolean(coverLetterApp)}
      onClose={() => setCoverLetterApp(null)}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: "600px",
          maxHeight: "80vh",
          overflowY: "auto",
          borderRadius: "10px",
          padding: "24px",
          mx: 2,
          outline: "none",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontFamily: "Segoe UI Semibold, system-ui, sans-serif", fontWeight: "800" }}>
              <FormattedMessage id="application.cover_letter" defaultMessage="Cover letter" />
            </Typography>
            {coverLetterApp?.job_offer?.job_title && (
              <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary }}>
                {coverLetterApp.job_offer.job_title}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={() => setCoverLetterApp(null)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-line", wordBreak: "break-word", color: theme.palette.text.strong }}
        >
          {coverLetterApp?.cover_letter}
        </Typography>
      </Paper>
    </Modal>
  );

  const contactCandidateModal = (
    <ContactCandidateModal
      open={isContactModalOpen}
      onClose={() => {
        setIsContactModalOpen(false);
        loadContactConversation();
      }}
      candidate={candidateData}
    />
  );

  if (!isModal) {
    return (
      <>
        {isDialogOpen ? dialogContent : null}
        {moveToModal}
        {hiredSelectionModal}
        {closeApplicationModal}
        {reopenApplicationModal}
        {coverLetterModal}
        {contactCandidateModal}
      </>
    );
  }

  return (
    <>
      <Modal
        open={isDialogOpen}
        onClose={handleClose}
        disableAutoFocus
        sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
        }}
      >
        {dialogContent}
      </Modal>
      {moveToModal}
      {hiredSelectionModal}
      {closeApplicationModal}
      {reopenApplicationModal}
      {coverLetterModal}
      {contactCandidateModal}
    </>
  );
};

export default CandidateDialog;
